import angular from 'angular';
import bcpDataService from '../data/bcp.data.service';
import orgDataService from '../data/org.data.service';
import ISharedModelService from './ISharedModel.service';
import { default as transformations } from './bcp.sharedModel.transformations';


let _bcpDataService;
let _orgDataService;
let _$q;

class bcpSharedModelService extends ISharedModelService {
  constructor(
    $q,
    bcpDataService, // eslint-disable-line no-shadow
    orgDataService // eslint-disable-line no-shadow
  ) {
    super('bcp');
    _$q = $q;
    _bcpDataService = bcpDataService;
    _orgDataService = orgDataService;

    console.log('app.services.bcpSharedModelService created');
  }

  allowNewAssessment() {
    return _bcpDataService.allowNewAssessment()
      .then(response => (response.data && response.data.result) || false)
      .catch(() => false);
  }

  getSchematics(assessmentId, forceRefresh) {
    return this.getNetwork(assessmentId, forceRefresh)
      .then(network => network.schematics);
  }

  getWorkstations(assessmentId, forceRefresh) {
    return this.getNetwork(assessmentId, forceRefresh)
      .then(network => network.workstations);
  }

  getServers(assessmentId, forceRefresh) {
    return this.getNetwork(assessmentId, forceRefresh)
      .then(network => network.servers);
  }

  getNetwork(assessmentId, forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.network) {
      this.cache.bcp.network = _bcpDataService.getNetwork(assessmentId)
        .then((response) => {
          const network = response.data;
          const schematics = this.sortByName(network.assessmentSchematics);
          const workstations = this.sortByName(network.assessmentWorkstations);
          const servers = this.sortByName(network.assessmentServers);

          return {
            schematics,
            workstations,
            servers,
          };
        });
    }
    return this.cache.bcp.network;
  }

  getSchematicSections(forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.schematicSections) {
      this.cache.bcp.schematicSections = _bcpDataService.getSchematicSections()
        .then(response => response.data.result);
    }
    return this.cache.bcp.schematicSections;
  }

  getSchematicSection(schematicSectionId, forceRefresh = false) {
    return this.getSchematicSections(forceRefresh)
      .then(sections => sections.find(section => section.schematicSectionId === schematicSectionId));
  }

  /**
   * Schematic object definition
   * @typedef {Object} schematic
   * @property {int} assessmentId
   * @property {int} assessmentSchematicId
   * @property {string} description - not used or input by UI at this time
   * @property {int} documentId
   * @property {string} mediaURL
   * @property {string} mimeType
   * @property {string} name
   * @property {int} schematicSectionId
   * @property {string} [sectionName]
   * @property {int} sortOrder
   */

  /**
   *
   * @param assessmentId
   * @param schematic
   * @returns {schematic}
   */
  uploadSchematic(assessmentId, schematic, sectionName) {
    return _bcpDataService.uploadSchematic(assessmentId, schematic)
      .then((response) => {
        const newSchematic = response.data;

        // TODO: Fix bug in API response where name is truncated file name instead of user provided, and more below
        newSchematic.assessmentId = assessmentId; // Temporary API name fix
        newSchematic.assessmentSchematicId = -1; // Temporary API name fix, need real value
        newSchematic.mediaURL = null; // Temporary API name fix, need real value
        newSchematic.name = schematic.name; // Temporary API name fix
        newSchematic.schematicSectionId = schematic.schematicSectionId; // Temporary API name fix
        newSchematic.sortOrder = 0; // Temporary API name fix, need real value
        newSchematic.sectionName = sectionName;
        return newSchematic;
      });
  }

  /**
   *
   * @param {string} assetName - Name of collection, initial letter uppercase, singular (not plural)
   * @param {int} assetCount - Length of the collection
   * @returns {string} - A phrase representing the status of the collection
   */
  getAssetStatus(assetName, assetCount) {
    switch (assetCount) {
      case 0:
        return `0 ${assetName}s Added`;
      case 1:
        return `1 ${assetName} Added`;
      default:
        return `${assetCount} ${assetName}s Added`;
    }
  }

  saveTest(assessmentTest) {
    if (assessmentTest.assessmentTestId > 0) {
      return _bcpDataService.updateTest(assessmentTest);
    }
    return _bcpDataService.addTest(assessmentTest);
  }

  /* *****************************************************************************
   *  Succession Planning
   */

  getSuccessionProcesses(assessmentId) {
    return _bcpDataService.getSuccessionProcesses(assessmentId)
      .then(({ data }) => {
        const processes = data.result;
        processes.forEach((process) => {
          process.successions = transformations.transformAssessmentSuccessionPlansToSuccessions(
            process.assessmentSuccessionPlans
          );
        });
        return processes;
      });
  }

  saveProcessSuccessions(assessmentProcessId, successions) {
    const apsp = transformations.transformSuccessionsToAssessmentSuccessionPlans(successions);
    return _bcpDataService.saveProcessSuccessions(assessmentProcessId, apsp);
  }

  /**
   *
   * @param {int} assessmentId
   * @param {int} assessmentProcessId
   * @param {int} assessmentProcessSuccessionPlanId
   * @return {Promise<[successor]>}
   */
  getSuccessorOptions(assessmentId, assessmentProcessId, assessmentProcessSuccessionPlanId) {
    const promises = [
      _orgDataService.getSelectableUsers(),
      this.getVendorResources(assessmentId),
    ];
    return _$q.all(promises).then((results) => {
      const users = results[0].data.result;
      const vendorResources = results[1];
      const vendorOptions = transformations.transformVendorResourceToSuccessor(
        assessmentProcessId,
        assessmentProcessSuccessionPlanId,
        vendorResources
      );
      const userOptions = transformations.transformUsersToSuccessors(
        assessmentProcessId,
        assessmentProcessSuccessionPlanId,
        users
      );
      const successors = vendorOptions.concat(userOptions);
      return this.sortByName(successors);
    });
  }


  /* *****************************************************************************
   *  Vendor Resources
   */

  getVendorResources(assessmentId, forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.schematicSections) {
      this.cache.bcp.schematicSections = _bcpDataService.getVendorResources(assessmentId)
        .then(response => response.data.result);
    }
    return this.cache.bcp.schematicSections;
  }

  getVendorResource(assessmentVendorResourceId, forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.schematicSections) {
      this.cache.bcp.schematicSections = _bcpDataService.getVendorResource(assessmentVendorResourceId)
        .then(response => response.data.result);
    }
    return this.cache.bcp.schematicSections;
  }

  saveVendorResource(vendorResource) {
    if (vendorResource.vendorId > 0) {
      return _bcpDataService.updateVendorResource(vendorResource);
    }
    return _bcpDataService.addVendorResource(vendorResource);
  }

  saveVendorResourcesList(assessmentId, vendorResourceIds) {
    return _bcpDataService.saveVendorResourcesList(assessmentId, vendorResourceIds);
  }

  deleteVendorResource(assessmentVendorResourceId) {
    return _bcpDataService.deleteVendorResource(assessmentVendorResourceId);
  }

  /* *****************************************************************************
   *  Other Resources
   */

  getOtherResources(assessmentId, forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.schematicSections) {
      this.cache.bcp.schematicSections = _bcpDataService.getOtherResources(assessmentId)
        .then(response => response.data.result);
    }
    return this.cache.bcp.schematicSections;
  }

  getOtherResource(assessmentOtherResourceId, forceRefresh = false) {
    if (forceRefresh || !this.cache.bcp.schematicSections) {
      this.cache.bcp.schematicSections = _bcpDataService.getOtherResource(assessmentOtherResourceId)
        .then(response => response.data.result);
    }
    return this.cache.bcp.schematicSections;
  }

  saveOtherResource(otherResource) {
    if (otherResource.assessmentOtherResourceId > 0) {
      return _bcpDataService.updateOtherResource(otherResource);
    }
    return _bcpDataService.addOtherResource(otherResource);
  }

  deleteOtherResource(assessmentOtherResourceId) {
    return _bcpDataService.deleteOtherResource(assessmentOtherResourceId);
  }
}

bcpSharedModelService.$inject = [
  '$q',
  'bcpDataService',
  'orgDataService'
];

export default angular.module('app.services.bcpSharedModelService', [
  bcpDataService,
  orgDataService
])
  .service('bcpSharedModelService', bcpSharedModelService)
  .name;
